/**
 * What is selected on the project architecture picture, and the words about it beside the picture. The workbench's
 * ProjectArchPanel is also where the picture is edited; on the console the picture is read here and drawn on a
 * member's workbench, so this says what a thing is, what it is connected to and where it lives, and nothing more.
 * `ArchSelection` is the workbench's own type, so ProjectArchPicture reads the same here as there.
 */
import { ArrowRight, Folder, LayoutCells, Link } from '@gravity-ui/icons';
import type { ReactNode } from 'react';

import { connectionCaption, connectionKindLabel, connectionsOf, endCaption, type ProjectArchModel } from './projectArchModel.ts';

export type ArchSelection =
  | { kind: 'area'; key: string }
  | { kind: 'connection'; index: number }
  | { kind: 'layer'; id: string; fresh?: boolean }
  | { kind: 'repository'; rootId: string }
  /** The form for a connection that is not in the document yet (the workbench's; the console never opens it). */
  | { kind: 'new-connection'; from?: string }
  | null;

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="grid gap-1">
      <span className="text-xs font-medium text-muted">{title}</span>
      {children}
    </div>
  );
}

/** The connections of an area or a repository, as lines a person can pick. */
function Connections({ list, onSelect }: { list: ReturnType<typeof connectionsOf>; onSelect(next: ArchSelection): void }) {
  if (!list.length) return <p className="m-0 text-sm text-muted">No connection to another repository yet.</p>;
  return (
    <ul className="m-0 grid list-none gap-1 p-0">
      {list.map((c) => (
        <li key={c.index}>
          <button type="button" className="flex w-full min-w-0 cursor-[var(--cursor-interactive)] items-center gap-1.5 rounded-lg border-0 bg-transparent px-0 py-0.5 text-left text-sm text-foreground hover:text-accent" onClick={() => onSelect({ kind: 'connection', index: c.index })}>
            <Link className="size-3.5 shrink-0 text-muted" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">{`${endCaption(c.from)} to ${endCaption(c.to)}`}</span>
            <span className="shrink-0 text-xs text-muted">{connectionCaption(c)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function ArchDetails({ model, selection, onSelect }: { model: ProjectArchModel; selection: ArchSelection; onSelect(next: ArchSelection): void }) {
  if (!selection || selection.kind === 'new-connection') {
    return (
      <div className="grid gap-2 text-sm text-muted" data-arch-details="none">
        <p className="m-0">
          {plural(model.counts.layers, 'layer')} across {plural(model.counts.repositories, 'repository', 'repositories')}, with {plural(model.counts.areas, 'area')} and {plural(model.counts.connections, 'connection')} between them.
        </p>
        <p className="m-0">Pick an area, a repository, a layer or a line to see what it is and what it is connected to.</p>
      </div>
    );
  }
  if (selection.kind === 'area') {
    const area = model.areas.get(selection.key);
    if (!area) return null;
    const repo = model.repositories.find((r) => r.rootId === area.rootId);
    const layer = model.layers.find((l) => l.id === area.layerId);
    return (
      <div className="grid gap-3" data-arch-details="area">
        <div>
          <h3 className="m-0 text-base font-semibold">{area.title}</h3>
          <p className="m-0 text-sm text-muted">{`${repo?.name ?? area.rootId} · ${area.path || 'the whole repository'}`}</p>
        </div>
        {area.description ? <p className="m-0 text-sm">{area.description}</p> : null}
        <Section title="Where it sits">
          <span className="text-sm">{layer ? `In the ${layer.title} layer${area.placedByHand ? ', put there by hand' : ''}` : 'In no layer of the project yet'}</span>
          {area.repositoryLayer ? <span className="text-sm text-muted">{`Its repository calls its layer ${area.repositoryLayer}`}</span> : null}
          {area.files !== undefined ? <span className="text-sm tabular-nums text-muted">{plural(area.files, 'file')}{area.symbols !== undefined ? ` · ${plural(area.symbols, 'symbol')}` : ''}</span> : null}
        </Section>
        <Section title="Connections">
          <Connections list={connectionsOf(model, { rootId: area.rootId, areaKey: area.key })} onSelect={onSelect} />
        </Section>
      </div>
    );
  }
  if (selection.kind === 'repository') {
    const repo = model.repositories.find((r) => r.rootId === selection.rootId);
    if (!repo) return null;
    const home = model.layers.find((l) => l.id === repo.homeLayer);
    return (
      <div className="grid gap-3" data-arch-details="repository">
        <div className="flex items-center gap-2">
          <Folder className="size-4 shrink-0 text-muted" aria-hidden="true" />
          <h3 className="m-0 min-w-0 truncate text-base font-semibold">{repo.name}</h3>
        </div>
        <p className="m-0 text-sm text-muted">{repo.known ? `${plural(repo.areaCount, 'area')}, mostly in ${home ? `the ${home.title} layer` : 'no layer yet'}.` : 'Nothing is known of its code yet: a member’s workbench shares its map once it has one.'}</p>
        <Section title="Connections">
          <Connections list={connectionsOf(model, { rootId: repo.rootId })} onSelect={onSelect} />
        </Section>
      </div>
    );
  }
  if (selection.kind === 'layer') {
    const layer = model.layers.find((l) => l.id === selection.id);
    if (!layer) return null;
    const areas = layer.repositories.reduce((n, g) => n + g.areas.length, 0);
    return (
      <div className="grid gap-3" data-arch-details="layer">
        <div className="flex items-center gap-2">
          <LayoutCells className="size-4 shrink-0 text-muted" aria-hidden="true" />
          <h3 className="m-0 min-w-0 truncate text-base font-semibold">{layer.title}</h3>
        </div>
        {layer.description ? <p className="m-0 text-sm">{layer.description}</p> : null}
        <p className="m-0 text-sm text-muted">{`${plural(areas, 'area')} from ${plural(layer.repositories.length, 'repository', 'repositories')}.`}</p>
      </div>
    );
  }
  const c = model.connections[selection.index];
  if (!c) return null;
  return (
    <div className="grid gap-3" data-arch-details="connection">
      <div>
        <h3 className="m-0 text-base font-semibold">{connectionCaption(c)}</h3>
        <p className="m-0 text-sm text-muted">{connectionKindLabel(c.kind)}</p>
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-sm">
        <button type="button" className="min-w-0 cursor-[var(--cursor-interactive)] truncate border-0 bg-transparent p-0 text-left text-foreground hover:text-accent" onClick={() => onSelect(c.from.areaKey ? { kind: 'area', key: c.from.areaKey } : { kind: 'repository', rootId: c.from.rootId })}>
          {endCaption(c.from)}
        </button>
        <ArrowRight className="size-3.5 shrink-0 text-muted" aria-label="to" />
        <button type="button" className="min-w-0 cursor-[var(--cursor-interactive)] truncate border-0 bg-transparent p-0 text-left text-foreground hover:text-accent" onClick={() => onSelect(c.to.areaKey ? { kind: 'area', key: c.to.areaKey } : { kind: 'repository', rootId: c.to.rootId })}>
          {endCaption(c.to)}
        </button>
      </div>
      {c.description ? <p className="m-0 text-sm">{c.description}</p> : null}
      {!c.valid ? <p className="m-0 text-sm text-warning">One end names a repository the project does not have.</p> : null}
    </div>
  );
}
